import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AlldoneIcon from '@mui/icons-material/TaskAlt';
import { isSilverPlan, isSilverPlanAvailable } from '../utils/subscriptionPlans';
import {
    PLATFORM_SUBSCRIBER_FEATURES,
    GOLD_PACKAGE,
    SILVER_PACKAGE,
} from '../utils/subscriptionPackageDetails';

function BenefitList({ items, iconColor = '#0ea5e9' }) {
    return (
        <List dense sx={{ py: 0 }}>
            {items.map((line) => (
                <ListItem key={line} disableGutters sx={{ py: 0.35, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 30, mt: 0.25 }}>
                        <AlldoneIcon sx={{ color: iconColor, fontSize: 18 }} />
                    </ListItemIcon>
                    <ListItemText
                        primary={line}
                        primaryTypographyProps={{ variant: 'body2', color: '#475569', lineHeight: 1.45 }}
                    />
                </ListItem>
            ))}
        </List>
    );
}

function PackageCard({ pkg, isCurrent, accent = '#0284c7' }) {
    return (
        <Box
            sx={{
                borderRadius: 2.5,
                border: '2px solid',
                borderColor: isCurrent ? accent : '#e2e8f0',
                bgcolor: isCurrent ? 'rgba(240, 249, 255, 0.9)' : '#f8fafc',
                p: 2,
                height: '100%',
                transition: 'border-color 0.2s, box-shadow 0.2s',
                boxShadow: isCurrent ? '0 4px 20px rgba(2, 132, 199, 0.12)' : 'none',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 0.75 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0f172a' }}>
                    {pkg.name}
                </Typography>
                {isCurrent ? <Chip label="Your plan" size="small" color="primary" sx={{ height: 22 }} /> : null}
            </Box>
            <Typography variant="body2" sx={{ color: '#0369a1', fontWeight: 600, mb: 0.5 }}>
                {pkg.tagline}
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 1.5, lineHeight: 1.5 }}>
                Best for: {pkg.bestFor}
            </Typography>
            <BenefitList items={pkg.includes} iconColor={isCurrent ? accent : '#94a3b8'} />
        </Box>
    );
}

export default function PlanDetailsSection({ school, admin }) {
    if (!school) return null;

    const silverAvailable = isSilverPlanAvailable(school, admin);
    const onSilver = isSilverPlan(school, admin);

    return (
        <Box
            sx={{
                mb: 3,
                p: { xs: 2, sm: 2.5 },
                borderRadius: 3,
                border: '1px solid #e2e8f0',
                bgcolor: '#fff',
                textAlign: 'left',
            }}
        >
            <Typography
                variant="overline"
                sx={{ color: '#94a3b8', fontWeight: 700, letterSpacing: 1, display: 'block', mb: 0.5 }}
            >
                Package details
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748b', mb: 2, lineHeight: 1.55 }}>
                Gold and Silver unlock the same platform. The difference is how you pay and how SMS works.
            </Typography>

            <Accordion
                disableGutters
                elevation={0}
                sx={{
                    border: '1px solid #e2e8f0',
                    borderRadius: '12px !important',
                    mb: 2,
                    '&:before': { display: 'none' },
                }}
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 48 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        All platform features ({PLATFORM_SUBSCRIBER_FEATURES.length})
                    </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                    <BenefitList items={PLATFORM_SUBSCRIBER_FEATURES} />
                </AccordionDetails>
            </Accordion>

            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', mb: 1.5 }}>
                Compare packages
            </Typography>

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: silverAvailable ? '1fr 1fr' : '1fr' },
                    gap: 2,
                }}
            >
                {silverAvailable ? (
                    <>
                        <PackageCard pkg={GOLD_PACKAGE} isCurrent={!onSilver} />
                        <PackageCard pkg={SILVER_PACKAGE} isCurrent={onSilver} accent="#0891b2" />
                    </>
                ) : (
                    <PackageCard pkg={GOLD_PACKAGE} isCurrent />
                )}
            </Box>
        </Box>
    );
}
